'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var React = require('react');
var client = require('@apollo/client');
var nthHookLoma = require('@narvar/nth-hook-loma');
var graphql = require('./graphql.js');
var proxyRetailerTheme = require('./proxyRetailerTheme.js');

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

var React__default = /*#__PURE__*/_interopDefaultLegacy(React);

const LomaThemeConfigContext = /*#__PURE__*/React__default["default"].createContext({
  product: 'narvarjs',
  retailerMoniker: 'DEFAULT_RETAILER',
  versionName: 'narvar'
});
const ThemeContext = /*#__PURE__*/React__default["default"].createContext(undefined);
const nullRetailerTheme = {}; // maintains object ref equality
const LomaThemeProvider = _ref => {
  let {
    children,
    featureName,
    themeTokens
  } = _ref;
  const client$1 = nthHookLoma.useLomaClient();
  const {
    product,
    retailerMoniker,
    versionName,
    localeLanguage,
    localeCountry,
    bannerName,
    cache,
    preventFouc,
    type
  } = React__default["default"].useContext(LomaThemeConfigContext);
  const {
    data,
    loading
  } = client.useQuery(graphql["default"](themeTokens.join('\n')), {
    client: client$1,
    variables: {
      product,
      retailerMoniker,
      versionName,
      localeLanguage,
      localeCountry,
      featureName,
      bannerName,
      cache,
      type
    }
  });
  const theme = (data === null || data === void 0 ? void 0 : data.retailerTheme) ?? nullRetailerTheme;
  const value = React__default["default"].useMemo(() => ({
    loading,
    theme: proxyRetailerTheme["default"](theme, themeTokens)
  }), [loading, theme, themeTokens]);
  return /*#__PURE__*/React__default["default"].createElement(ThemeContext.Provider, {
    value: value
  }, loading && preventFouc ? /*#__PURE__*/React__default["default"].createElement("span", {
    style: {
      visibility: 'hidden'
    }
  }, children) : children);
};
const ThemeProviderContext = /*#__PURE__*/React__default["default"].createContext(LomaThemeProvider);
const LomaThemeConfigProvider = _ref2 => {
  let {
    children,
    ...props
  } = _ref2;
  return /*#__PURE__*/React__default["default"].createElement(ThemeProviderContext.Provider, {
    value: LomaThemeProvider
  }, /*#__PURE__*/React__default["default"].createElement(LomaThemeConfigContext.Provider, {
    value: props
  }, children));
};
const ThemeProvider = props => {
  const {
    themeTokens
  } = props;
  if (!themeTokens.length) throw new Error('themeTokens must be non-empty');
  const ThemeProvider = React__default["default"].useContext(ThemeProviderContext);
  return /*#__PURE__*/React__default["default"].createElement(ThemeProvider, props);
};
const withThemeProvider = (component, options) => {
  const {
    themeTokens
  } = options;
  const HoC = /*#__PURE__*/React__default["default"].forwardRef((props, ref) => {
    const {
      featureName
    } = props;
    return /*#__PURE__*/React__default["default"].createElement(ThemeProvider, {
      featureName,
      themeTokens
    }, /*#__PURE__*/React__default["default"].createElement(component, {
      ...props,
      ref
    }));
  });
  const name = component.displayName ?? component.name;
  HoC.displayName = `WithThemeProvider(${name})`;
  return HoC;
};

/**
 * Optimal usage of `useTheme` requires destructuring properties immediately:
 *
 * ```
 * const { bodytextDefaultNone, bodytextEmphasisNone } = useTheme();
 * ```
 */
const useTheme = () => {
  const context = React__default["default"].useContext(ThemeContext);
  if (!context) throw new Error('useTheme was called without a ThemeContext');
  return context.theme;
};

/**
 * Optimal usage of `useThemeX` requires destructuring properties immediately:
 *
 * ```
 * const {
 *   loading,
 *   theme: {
 *     bodytextDefaultNone,
 *     bodytextEmphasisNone
 *   }
 * } = useThemeX();
 * ```
 */
const useThemeX = () => {
  const context = React__default["default"].useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeX was called without a ThemeContext');
  }
  return context;
};
const LocalThemeConfigContext = /*#__PURE__*/React__default["default"].createContext({
  queryTheme: () => {
    throw new Error('Not implemented');
  }
});
const LocalThemeProvider = _ref3 => {
  let {
    children,
    ...props
  } = _ref3;
  const config = React__default["default"].useContext(LocalThemeConfigContext);
  const value = config.queryTheme(props);
  return /*#__PURE__*/React__default["default"].createElement(ThemeContext.Provider, {
    value: value
  }, children);
};
const LocalThemeConfigProvider = _ref4 => {
  let {
    children,
    ...props
  } = _ref4;
  return /*#__PURE__*/React__default["default"].createElement(ThemeProviderContext.Provider, {
    value: LocalThemeProvider
  }, /*#__PURE__*/React__default["default"].createElement(LocalThemeConfigContext.Provider, {
    value: props
  }, children));
};

exports.retailerThemeQuery = graphql["default"];
exports.LocalThemeConfigProvider = LocalThemeConfigProvider;
exports.LocalThemeProvider = LocalThemeProvider;
exports.LomaThemeConfigProvider = LomaThemeConfigProvider;
exports.ThemeProvider = ThemeProvider;
exports.useTheme = useTheme;
exports.useThemeX = useThemeX;
exports.withThemeProvider = withThemeProvider;
